import { useEffect, useState } from 'react';

import { dequal } from 'dequal';

//types
import type {
    RequestParamsType,
    SetRequestParamsType
} from '@/utils/hooks/swr/reports/useFetchReports';
// Hooks
import useSocialProfiles from '@/utils/hooks/swr/useSocialProfiles';
import useTranslation from '@/utils/hooks/translation/useTranslation';

import type { SocialProfileType } from '@/types/socialProfileType';

// Lib
import { formatSocialProfileType } from '@/lib/socialProfile';

// Components
import Select from '@/components/base/Select';

type OptionType = {
    value: string | undefined; // I'm not using the type SocialProfileType['id'] because before that the Select component need to be reworked with Generics
    label: string;
};

type SelectSocialProfilesFilterProps = {
    requestParams: RequestParamsType;
    setRequestParams: SetRequestParamsType;
};

const SelectSocialProfilesFilter = ({
    requestParams,
    setRequestParams
}: SelectSocialProfilesFilterProps) => {
    const { t } = useTranslation();
    const defaultOptions: OptionType[] = [
        { value: undefined, label: t('reports:general.all_social_profiles') }
    ];
    const [options, setOptions] = useState(defaultOptions);
    const { activatedSocialProfiles } = useSocialProfiles({
        revalidateIfStale: false,
        shouldRetryOnError: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false
    });
    const [reportsSocialProfiles, setReportSocialProfiles] = useState<SocialProfileType['id'][]>(
        []
    );

    useEffect(() => {
        if (activatedSocialProfiles?.length) {
            setOptions((prevState) => [
                ...prevState,
                ...activatedSocialProfiles.map((socialProfile) => ({
                    value: socialProfile?.id.toString(),
                    //Truncate option here to prevent Network indicator to be cropped
                    label: `${
                        socialProfile?.slug?.length > 13
                            ? `${socialProfile?.slug?.substring(0, 13)}...`
                            : socialProfile?.slug
                    } (${formatSocialProfileType(socialProfile)})`
                }))
            ]);
            return setReportSocialProfiles(activatedSocialProfiles?.map((el) => el?.id));
        }
        //Only reset options if activatedSocialProfiles is empty and options is not equal to defaultOptions
        if (!activatedSocialProfiles?.length && !dequal(options, defaultOptions)) {
            setOptions(defaultOptions);
        }
    }, [JSON.stringify(activatedSocialProfiles)]);

    useEffect(() => {
        if (activatedSocialProfiles?.length) {
            if (
                requestParams?.filter_social_profile &&
                !reportsSocialProfiles?.includes(requestParams?.filter_social_profile)
            ) {
                setRequestParams((prevState) => ({
                    ...prevState,
                    page: 1,
                    filter_social_profile: undefined
                }));
            }
        }
    }, [JSON.stringify(activatedSocialProfiles)]);

    const selectedOptionValue = reportsSocialProfiles?.includes(
        requestParams?.filter_social_profile
    )
        ? requestParams?.filter_social_profile.toString()
        : undefined;

    return (
        <Select
            buttonBaseStyle="noBorder"
            className="w-full @2xl:w-44"
            isTruncate={false}
            setSelected={(option: OptionType) =>
                setRequestParams((prevState) => ({
                    ...prevState,
                    page: 1,
                    filter_social_profile: option.value ? Number(option.value) : undefined
                }))
            }
            selected={options?.filter((item) => item?.value === selectedOptionValue)?.[0]}
            options={options}
            forcedDisplay
            data-testid="select-social-profiles-filter"
        />
    );
};

export default SelectSocialProfilesFilter;
